import React, { useState, useMemo, useRef, useEffect } from 'react';
import { Popover, PopoverContent, PopoverTrigger } from '../components/ui/popover';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { ScrollArea } from '../components/ui/scroll-area';
import { ChevronDown, Search, Clock, Check } from 'lucide-react';
import { getTimezoneLabel, searchTimezones } from '../utils/timezoneList';

/**
 * Searchable timezone selector. Reusable across Settings, Device Management, Global Settings.
 * @param {Object} props
 * @param {string} props.value - Current IANA timezone value
 * @param {function} props.onChange - Callback with new value
 * @param {boolean} [props.includeAuto=false] - Show "Auto (GPS Location)" option (device settings only)
 */
export const TimezoneSelector = ({ value, onChange, includeAuto = false, triggerClassName = '' }) => {
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState('');
  const searchRef = useRef(null);

  useEffect(() => {
    if (open && searchRef.current) {
      setTimeout(() => searchRef.current?.focus(), 100);
    }
  }, [open]);

  // Reset the search box in the open-change handler rather than an effect
  // (avoids react-hooks/set-state-in-effect — clearing on close is event-driven).
  const handleOpenChange = (next) => {
    setOpen(next);
    if (!next) setSearch('');
  };

  const filtered = useMemo(
    () => searchTimezones(search, { includeAuto }),
    [search, includeAuto]
  );

  const displayLabel = getTimezoneLabel(value);

  return (
    <Popover modal={true} open={open} onOpenChange={handleOpenChange}>
      <PopoverTrigger asChild>
        <Button
          variant="outline"
          role="combobox"
          aria-expanded={open}
          className={`w-full justify-between font-normal ${triggerClassName}`}
          data-testid="timezone-selector-trigger"
        >
          <span className="flex items-center gap-2 truncate">
            <Clock className="w-4 h-4 text-muted-foreground shrink-0" />
            <span className="truncate">{displayLabel}</span>
          </span>
          <ChevronDown className="w-4 h-4 text-muted-foreground shrink-0 ml-2" />
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-[380px] p-0 z-[10000]" align="start" data-testid="timezone-selector-dropdown">
        <div className="p-2 border-b">
          <div className="relative">
            <Search className="absolute left-2.5 top-2.5 w-4 h-4 text-muted-foreground" />
            <Input
              ref={searchRef}
              placeholder="Search by country or city..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="pl-8 h-9"
              data-testid="timezone-search-input"
            />
          </div>
        </div>
        <ScrollArea className="h-[300px]">
          {filtered.length === 0 && (
            <div className="p-4 text-sm text-muted-foreground text-center">No timezones found</div>
          )}
          {filtered.map(tz => (
            <button
              key={tz.value}
              onClick={() => { onChange(tz.value); setOpen(false); }}
              className={`w-full text-left px-3 py-2 text-sm flex items-center justify-between hover:bg-accent transition-colors ${
                value === tz.value ? 'bg-accent/50 font-medium' : ''
              }`}
              data-testid={`timezone-option-${tz.value}`}
            >
              <span className="truncate">{tz.label}</span>
              {value === tz.value && <Check className="w-4 h-4 text-[#2563eb] shrink-0 ml-2" />}
            </button>
          ))}
        </ScrollArea>
      </PopoverContent>
    </Popover>
  );
};

export default TimezoneSelector;
